import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated blog posts list with tag filtering
 */
export default class BlogPostsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    // Disable automatic infinite scroll
    this.#disableAutoScroll();

    // Initialize manual load more button
    this.#initManualLoadMore();

    // Initialize tag filtering
    this.#initTagFiltering();
  }

  /**
   * Disconnect the IntersectionObserver to disable automatic loading
   */
  #disableAutoScroll() {
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
      this.infinityScrollObserver = null;
    }
  }

  /**
   * Set up manual click handler for Load More button
   */
  #initManualLoadMore() {
    const loadMoreButton = this.querySelector('[data-load-more-button]');
    if (!loadMoreButton) return;

    loadMoreButton.addEventListener('click', async () => {
      await this.#loadNextPage();
    });
  }

  /**
   * Manually load the next page of blog posts
   */
  async #loadNextPage() {
    const { grid } = this.refs;
    const { cards } = this.refs;

    if (!grid || !Array.isArray(cards) || cards.length === 0) return;

    // Get current last card's page number
    const lastCard = cards[cards.length - 1];
    const currentPage = Number(lastCard.dataset.page);
    const lastPage = Number(grid.dataset.lastPage);
    const nextPage = currentPage + 1;

    // Check if there are more pages
    if (nextPage > lastPage) {
      this.#hideLoadMoreButton();
      return;
    }

    // Show loading state
    const loadMoreButton = this.querySelector('[data-load-more-button]');
    if (loadMoreButton) {
      loadMoreButton.disabled = true;
      loadMoreButton.textContent = 'Loading...';
    }

    try {
      // Fetch the next page
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage.toString());
      url.hash = '';

      const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, url);

      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');

      // Extract the new page's cards
      const newGrid = doc.querySelector('[ref="grid"]');
      if (!newGrid) return;

      const newCards = newGrid.querySelectorAll(':scope > [ref="cards[]"]');
      if (newCards.length === 0) return;

      // Append new cards to the grid
      grid.append(...newCards);

      // Update URL without page reload (clean URL without section_id)
      const historyUrl = new URL(window.location.href);
      historyUrl.searchParams.set('page', nextPage.toString());
      historyUrl.searchParams.delete('section_id');
      historyUrl.hash = '';
      history.pushState('', '', historyUrl.toString());

      // Check if this was the last page
      if (nextPage >= lastPage) {
        this.#hideLoadMoreButton();
      }
    } catch (error) {
      console.error('Failed to load more blog posts:', error);
    } finally {
      // Restore button state
      if (loadMoreButton) {
        loadMoreButton.disabled = false;
        loadMoreButton.textContent = loadMoreButton.getAttribute('data-original-text') || 'Load More';
      }
    }
  }

  /**
   * Hide the Load More button when no more pages are available
   */
  #hideLoadMoreButton() {
    const loadMoreContainer = this.querySelector('.blog-load-more');
    if (loadMoreContainer) {
      loadMoreContainer.style.display = 'none';
    }
  }

  #initTagFiltering() {
    /** @type {NodeListOf<HTMLAnchorElement>} */
    const tagLinks = this.querySelectorAll('.blog-tag');

    tagLinks.forEach((link) => {
      link.addEventListener('click', async (event) => {
        event.preventDefault();

        const targetUrl = new URL(link.href);

        // Update history
        history.pushState({}, '', targetUrl.toString());

        // Fetch and render the filtered section
        await this.#renderFilteredBlog(targetUrl);
      });
    });

    // Handle browser back/forward navigation
    window.addEventListener('popstate', async () => {
      await this.#renderFilteredBlog(new URL(window.location.href));
    });
  }

  /**
   * @param {URL} url
   */
  async #renderFilteredBlog(url) {
    try {
      // Fetch the filtered blog section HTML
      const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, true, url);

      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');

      // Extract the new section content
      const newSection = doc.querySelector('blog-posts-list');

      if (newSection) {
        // Replace the entire section content
        this.innerHTML = newSection.innerHTML;

        // Disable automatic infinite scroll
        this.#disableAutoScroll();

        // Reinitialize manual load more button
        this.#initManualLoadMore();

        // Reinitialize tag filtering for the new content
        this.#initTagFiltering();

        // Clear cached pages and dispatch filter update event
        this.pages.clear();
        document.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));

        // Scroll to top of section smoothly
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error('Failed to filter blog posts:', error);
    }
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
